/**
 * @file App.js
 * @description Root application component. Manages authentication state, role-based view
 *   routing, URL deep-link handling, and renders the global layout including TopNav,
 *   the desktop Inbox panel, and the onboarding flow.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module App
 */
import { useState, useEffect, useRef, useCallback, Suspense } from "react";
import { pathForView } from "./lib/viewPath";
import { captureApplicantLink, takeApplicantLink } from "./lib/applicantDeepLink";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { useTranslation } from "react-i18next";
import TopNav          from "./components/TopNav";
import { AGENCY_PAGE_VIEWS } from "./lib/agencyPages";
import { XIcon }               from "./components/Icons";
import HomeV2                  from "./components/HomeV2";
import TherapistLanding        from "./components/TherapistLanding";
import AgencyLanding           from "./components/AgencyLanding";
import CityRolePage            from "./components/CityRolePage";
import AboutAbaCases           from "./components/AboutAbaCases";
import AbaCaseFinderAlternative from "./components/AbaCaseFinderAlternative";
import AbaTimesheet            from "./components/AbaTimesheet";
import RbtTimesheet            from "./components/RbtTimesheet";
import AbaPaymentTracker       from "./components/AbaPaymentTracker";
import PostAbaCases            from "./components/PostAbaCases";
import PostACase               from "./components/PostACase";
import AbaStaffingForSmallAgencies from "./components/AbaStaffingForSmallAgencies";
import AbaStaffingFlorida      from "./components/AbaStaffingFlorida";
import AbaProviderWaitlist     from "./components/AbaProviderWaitlist";
import RbtStaffingCharlotteNc  from "./components/RbtStaffingCharlotteNc";
import RbtStaffingMiamiFl      from "./components/RbtStaffingMiamiFl";
import HowToBecomeAnRbt        from "./components/HowToBecomeAnRbt";
import RbtResumeGuide          from "./components/RbtResumeGuide";
import RbtInterviewQuestions   from "./components/RbtInterviewQuestions";
import HowToChooseAnAbaCase    from "./components/HowToChooseAnAbaCase";
import TravelRbtJobs           from "./components/TravelRbtJobs";
import RbtVsBtVsBcbaVsBcaba    from "./components/RbtVsBtVsBcbaVsBcaba";
import AbaResourcesHub         from "./components/AbaResourcesHub";
import AbaJobsByState          from "./components/AbaJobsByState";
import HowToBecomeABcba        from "./components/HowToBecomeABcba";
import HowToBecomeABcaba       from "./components/HowToBecomeABcaba";
import AbaRolesGuide           from "./components/AbaRolesGuide";
import { track, A }   from "./lib/analytics";
import { SEO_LANDING_SLUGS }   from "./data/seoLanding";
import LegalPage                from "./components/LegalPage";
import SupportPage              from "./components/SupportPage";
import EmailVerificationPending from "./components/EmailVerificationPending";
import AccountDeletionPending   from "./components/AccountDeletionPending";
import SuspendedAgencyScreen    from "./components/SuspendedAgencyScreen";
import ResetPasswordScreen      from "./components/ResetPasswordScreen";
import { BestMatchesTeaser, MyApplicationsTeaser } from "./components/GuestTeasers";
import { useAuth }             from "./hooks/useAuth";
import { agencyResume } from "./lib/onboardingResume";
import { loadTherapistProfile, loadAgencyProfile, saveTherapistProfile, saveAgencyProfile, markUserOnboarded, requestVerification } from "./lib/profileService";
import { validateCredentialFormat } from "./lib/credentialFormat";
import { createNotification } from "./lib/notificationService";
import { loadCaseById, resolveOpenCaseIdByCode } from "./lib/caseService";
import { shortLinkCode } from "./lib/caseCode";
import { loadAllConfigs, getCachedConfig } from "./lib/configService";
import { getAgencyPricing } from "./lib/agencyPricing";
import CaseFinderGate from "./components/CaseFinderGate";
import { supabase } from "./lib/supabase";
import { useIsMobile }         from "./hooks/useIsMobile";
import { T }                   from "./theme";
import ErrorBoundary from "./components/ErrorBoundary";
import { StripeCheckoutHost, PaymentMethodHost } from "./components/StripeHosts";
import { captureError } from "./lib/errorService";
import { activationCallsDue, runActivationCalls } from "./lib/accountActivation";
import { verifyLandingAction } from "./lib/verifyLanding";
import { isTherapistRole } from "./lib/roles";
import { getAcceptanceStatus } from "./lib/legalAcceptanceService";
import AcceptanceGate from "./components/AcceptanceGate";
import PayTrackerLanding from "./components/PayTrackerLanding";
import InterviewRespondPage from "./components/InterviewRespondPage";
import { isInterviewRespondPath } from "./lib/interviewRespond";
import CaseRespondPage from "./components/CaseRespondPage";
import { isCaseRespondPath } from "./lib/caseRespond";
import UnsubscribePage from "./components/UnsubscribePage";
import { isUnsubscribePath } from "./lib/unsubscribe";
import TeamJoinPage from "./components/TeamJoinPage";
import TeamAccessScreen, { OwnerOnlyNotice, OwnerOnlyDialog } from "./components/team/TeamAccessScreen";
import { TeamNamesProvider } from "./components/team/TeamNames";
import { isTeamJoinPath, loadAgencyContext, actingAgencyId, isTeamMember, canManageAgency, OWNER_ONLY_VIEWS } from "./lib/agencyContext";
import { startActivityPing } from "./lib/activityPing";
import { maybeNotifyTrackerDiscovery } from "./lib/trackerDiscovery";
import { clearPendingCase, clearSignupIntent, consumeSignupIntent, peekPendingCase, peekSignupIntent, setSignupIntent } from "./lib/signupIntent";
import { trackApplyResumeMissed, trackVerifyLoginPrompt } from "./lib/signupFunnel";

// Heavy signed-in screens load on demand so public pages (home, city pages, guides, legal) never download them.
const AuthScreen = lazyWithRetry(() => import("./components/AuthScreen"));
const RBTProfile = lazyWithRetry(() => import("./components/RBTProfile"));
const Billing = lazyWithRetry(() => import("./components/Billing"));
const AgencyDashboard = lazyWithRetry(() => import("./components/AgencyDashboard"));
const AdminPanel = lazyWithRetry(() => import("./components/AdminPanel"));
const MyApplications = lazyWithRetry(() => import("./components/MyApplications"));
const AgencyAbout = lazyWithRetry(() => import("./components/AgencyAbout"));
const Inbox = lazyWithRetry(() => import("./components/Inbox"));
const Onboarding = lazyWithRetry(() => import("./components/Onboarding"));
const FindCasesV2 = lazyWithRetry(() => import("./components/FindCasesV2"));
const SharePreviewPage = lazyWithRetry(() => import("./components/SharePreviewPage"));
const NotificationPreferences = lazyWithRetry(() => import("./components/NotificationPreferences"));
const Tracker = lazyWithRetry(() => import("./components/tracker/Tracker"));
const TeamPage = lazyWithRetry(() => import("./components/team/TeamPage"));


// Guest pages aimed at agencies (AGENCY_PAGE_VIEWS, from lib/agencyPages): a top-nav signup started on one is an agency signup.

// Views each role is allowed to land on (used for deep-link validation on refresh)
// "terms-of-use", "privacy-policy", "retention", and "support" are public —
// added to every role set so direct URL visits work (e.g. Stripe's Support/
// Terms/Privacy business-info links point straight at these).
const LEGAL_VIEWS = ["terms-of-use", "privacy-policy", "retention", "support"];
const ROLE_VIEWS = {
  agency: new Set(["dashboard","billing","agencyabout","team","inbox","notifications","share-preview",...LEGAL_VIEWS]),
  admin:  new Set(["admin","inbox","share-preview",...LEGAL_VIEWS]),
  rbt:    new Set(["cases-v2","profile","matches","applications","tracker","inbox","notifications","share-preview",...LEGAL_VIEWS]),
  bcba:   new Set(["cases-v2","profile","matches","applications","tracker","inbox","notifications","share-preview",...LEGAL_VIEWS]),
  bcaba:  new Set(["cases-v2","profile","matches","applications","tracker","inbox","notifications","share-preview",...LEGAL_VIEWS]),
  guest:  new Set(["home","free-aba-timesheet-payment-tracker","aba-case-finder","aba-agencies","about-aba-cases","aba-case-finder-alternative","aba-timesheet","rbt-timesheet","aba-payment-tracker","post-aba-cases","post-a-case","aba-staffing-for-small-agencies","aba-staffing-florida","aba-provider-waitlist","rbt-staffing-charlotte-nc","rbt-staffing-miami-fl","aba-jobs-by-state","how-to-become-an-rbt","rbt-resume-guide","rbt-interview-questions","how-to-choose-an-aba-case","travel-rbt-jobs","rbt-vs-bt-vs-bcba-vs-bcaba","aba-resources","how-to-become-a-bcba","how-to-become-a-bcaba","aba-roles-guide","cases-v2","matches","applications","auth","share-preview",...SEO_LANDING_SLUGS,...LEGAL_VIEWS]),
};

// Full-page URL aliases — a retired/renamed view id resolves to its current
// canonical page (no home-anchor scrolling). Covers the renamed tracker slug,
// the retired therapist-landing/agency-landing routes (now /aba-case-finder
// and /aba-agencies), and the old short-form legal slugs (now the Stripe-
// friendly /terms-of-use and /privacy-policy). /pay-tracker is also 301'd
// server-side in public/_redirects; these handle in-app nav + client deep links.
const PAGE_ALIASES = {
  "pay-tracker": "free-aba-timesheet-payment-tracker",
  "therapist-landing": "aba-case-finder",
  "agency-landing": "aba-agencies",
  "for-agencies": "aba-agencies",
  // Retired SEO page (merged into /aba-agencies); also 301'd at the edge in public/_redirects.
  "aba-staffing-for-agencies": "aba-agencies",
  // Retired city pages (merged into the ABA cases twins; same intent, only the role word differed); also 301'd at the edge.
  "aba-therapist-jobs-miami-fl": "aba-cases-miami-fl",
  "aba-therapist-jobs-charlotte-nc": "aba-cases-charlotte-nc",
  "terms": "terms-of-use",
  "privacy": "privacy-policy",
};

// The prerendered SEO routes are served at a trailing slash (/aba-agencies/, the
// non-slash form 308-redirects to it). Strip that slash when deriving the view
// id from the URL — otherwise a crawler landing on /aba-agencies/ yields the view
// "aba-agencies/", which isn't in any ROLE_VIEWS set, so the app falls through to
// a client-side redirect (to /home) that Google reports as a "Redirect error".
const viewFromPath = () => window.location.pathname.slice(1).replace(/\/+$/, "") || null;
// Path equality that ignores a trailing slash, so booting on the canonical
// /aba-agencies/ doesn't rewrite the URL to the non-slash /aba-agencies.
const samePath = (a, b) => (a || "").replace(/\/+$/, "") === (b || "").replace(/\/+$/, "");

/**
 * @description Returns true if a therapist profile has the minimum required fields
 *   to participate in Best Matches scoring (skills, availability, settings).
 * @param {Object|null} profile - The merged therapist profile object
 * @returns {boolean} Whether the profile is considered complete
 */
function isProfileComplete(profile) {
  if (!profile) return false;
  return (
    (profile.skills || []).length > 0 &&
    (profile.availability || []).length > 0 &&
    (profile.settings || []).length > 0
  );
}

export default function App() {
  const { t, i18n } = useTranslation();

  // ── Supabase auth state ──
  const { session, user, profile: supabaseProfile, loading: authLoading, setProfile: setSupabaseProfile, signOut, recoveryMode, setRecoveryMode } = useAuth();

  const isMobile = useIsMobile();

  // ── App UI state ──
  const [userRole, setUserRole]               = useState("guest");
  useEffect(() => { userRoleRef.current = userRole; }, [userRole]);
  const [view, _setView]                      = useState("auth");

  // Capture the URL path on first load so auth effect can honour deep links
  const initialUrlView = useRef(viewFromPath());

  // Read inside setView without making it re-identify — it is handed to dozens
  // of components as onNavigate, so its identity must stay stable.
  const userRoleRef            = useRef("guest");
  const publicCaseBrowsingRef  = useRef(false);

  // User-initiated navigation — adds an entry to browser history
  // Teammates are never sent to admin-only screens (Billing, buy credits, agency profile, Team): a dialog says
  // so and the page stays put, so browser Back keeps working. The server enforces the same rule.
  const teamMemberRef = useRef(false);
  const [ownerOnlyAsk, setOwnerOnlyAsk] = useState(false);
  const setView = useCallback((v) => {
    if (teamMemberRef.current && OWNER_ONLY_VIEWS.has(PAGE_ALIASES[v] || v)) { setOwnerOnlyAsk(true); return; }
    // Renamed/retired view ids → their current canonical page (e.g. pay-tracker
    // → the long tracker slug; therapist-landing → aba-case-finder).
    if (PAGE_ALIASES[v]) v = PAGE_ALIASES[v];
    // While public browsing is gated, send guests who click a "browse cases"
    // CTA straight to signup rather than bouncing them off the gate. The gate
    // still renders for direct /cases-v2 hits (bookmarks, typed URLs, old
    // links), so it stays as the safety net — it just is not in the way of
    // anyone who arrived through the product.
    if (v === "cases-v2" && userRoleRef.current === "guest" && !publicCaseBrowsingRef.current) {
      setAuthInitialMode("signup");
      v = "auth";
    }
    _setView(v);
    if (!samePath(window.location.pathname, pathForView(v))) {
      window.history.pushState({ view: v }, "", pathForView(v));
    }
  }, []);

  // Handle browser back / forward buttons
  useEffect(() => {
    function onPop(e) {
      let v = e.state?.view || viewFromPath() || "cases-v2";
      if (PAGE_ALIASES[v]) v = PAGE_ALIASES[v]; // back/forward to a renamed/retired route
      if (v === "es" || window.location.pathname === "/es") { // Spanish home twin
        try { i18n.changeLanguage("es"); } catch { /* ignore */ }
        v = "home";
      }
      _setView(v); // update state only — URL is already correct
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [i18n]);

  // Sync UI language preference to DB so transactional emails go out in the
  // right language. Fires whenever the user changes language via the switcher.
  useEffect(() => {
    const handler = async (lang) => {
      const userId = user?.id;
      if (!userId) return;
      const normalized = lang.startsWith("es") ? "es" : "en";
      try { await supabase.from("profiles").update({ preferred_language: normalized }).eq("id", userId); } catch (_) {}
    };
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, [user?.id, i18n]);

  const [showJobAlerts,       setShowJobAlerts]       = useState(false);
  const [alertInitialFilters, setAlertInitialFilters] = useState(null);
  const [userProfile, setUserProfile]         = useState(null);
  const [showOnboarding, setShowOnboarding]   = useState(false);
  // Agency onboarding resumes from what is already saved (NPI stamp, profile fields)
  // instead of restarting at step 1. null until the saved row has been read; the modal
  // waits for it so it never flashes an empty step 1 first.
  const [agencyResumeState, setAgencyResumeState] = useState(null);
  // A fresh page load (a reload, or coming back from another app) with a case remembered from a guest Apply tap opens
  // Sign up, not Log in: that visitor was on the way to creating a profile (lib/signupIntent pending case).
  const [authInitialMode, setAuthInitialMode] = useState(() => (peekPendingCase() ? "signup" : "login"));

  // ── Legal acceptance gate (v2.0 ToS + Privacy Policy) ──
  // After signup/login, check whether the user has accepted the current
  // versions of ToS and PP. If not (either never accepted or stale version
  // after a doc bump), block the UI with <AcceptanceGate> until they do.
  // null = not checked yet; true = needs to accept; false = current.
  const [needsLegalAcceptance, setNeedsLegalAcceptance] = useState(null);

  // ── Email verification config + gate ──
  // Default TRUE (fail closed): until loadAllConfigs() resolves we treat
  // verification as required. Otherwise a hard refresh — where the cached
  // session loads before the config fetch — would evaluate the gate with a
  // stale `false`, take the "verification is OFF" branch, and auto-stamp the
  // user verified WITHOUT the email link ever being clicked. The real value
  // is set once config loads (and stays TRUE if the fetch fails).
  const [requireEmailVerification, setRequireEmailVerification] = useState(true);
  // Guest access to the case finder. Defaults FALSE (gated) and fails closed:
  // if the config fetch never resolves we show the signup gate rather than an
  // empty map, because an empty marketplace shown to a stranger loses them.
  const [publicCaseBrowsing, setPublicCaseBrowsing] = useState(false);
  const [tokenVerifying, setTokenVerifying]   = useState(false);
  const [tokenResult,    setTokenResult]      = useState(null); // { ok, reason }
  // The opening a new provider signed up to apply to could not be reopened (closed or hidden meanwhile): its code,
  // shown in a banner over Find Cases until dismissed. null = nothing to say.
  const [resumeMissed,   setResumeMissed]     = useState(null);
  // Translation key of a note shown on the login screen ("Email verified! Log in to finish..."), cleared when they
  // leave it. Translated at render so it follows the current language.
  const [authNotice,     setAuthNotice]       = useState(null);

  // Auto-dismiss the success confirmation — it's a transient "nice to know",
  // the app already continues past it. Error banners (invalid/expired/used)
  // stay until manually closed so the user can act on them. A verified link
  // opened while logged out goes to the login screen with its own note (authNotice).
  useEffect(() => {
    if (tokenResult?.ok) {
      const id = setTimeout(() => setTokenResult(null), 4000);
      return () => clearTimeout(id);
    }
  }, [tokenResult]);

  // The login screen note belongs to that visit only.
  useEffect(() => { if (view !== "auth") setAuthNotice(null); }, [view]);

  // Load platform config once (needed for the email-verification gate)
  useEffect(() => {
    loadAllConfigs().then(() => {
      setRequireEmailVerification(!!getCachedConfig("require_email_verification"));
      setPublicCaseBrowsing(!!getCachedConfig("public_case_browsing"));
      publicCaseBrowsingRef.current = !!getCachedConfig("public_case_browsing");
    }).catch(() => {});
  }, []);

  // P1-8: Global Privacy Control (GPC) signal detection.
  // Privacy Policy §10 commits to honoring GPC as an opt-out of sale / targeted
  // advertising in states that require it (CO CPA, CT CTDPA). Since we don't sell
  // data, this is a behavioral no-op but we must detect and record the signal.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.globalPrivacyControl) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user?.id) return;
      // Write at most once per session per user. Previously this fired an
      // unconditional UPDATE to profiles on every page load for GPC users —
      // each write triggers a WAL event + RLS re-evaluation. The flag makes
      // it idempotent within the session; the value itself only ever flips
      // to true, so re-writing on later loads was pure waste.
      const guardKey = `gpc:recorded:${session.user.id}`;
      try { if (sessionStorage.getItem(guardKey)) return; } catch { /* ignore */ }
      supabase.from("profiles")
        .update({ gpc_opt_out: true })
        .eq("id", session.user.id)
        .then(() => { try { sessionStorage.setItem(guardKey, "1"); } catch { /* ignore */ } })
        .catch(() => {});
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── ?reactivate= URL handler — account reactivation deep link ──
  const [reactivating, setReactivating]     = useState(false);
  const [reactivateResult, setReactivateResult] = useState(null); // { ok, reason }

  useEffect(() => {
    const reactivateToken = new URLSearchParams(window.location.search).get("reactivate");
    if (!reactivateToken) return;
    window.history.replaceState({}, "", window.location.pathname);
    setReactivating(true);
    supabase.rpc("reactivate_account", { p_token: reactivateToken })
      .then(({ data, error }) => {
        const result = error
          ? { ok: false, reason: "rpc_error" }
          : (data?.ok ? { ok: true } : { ok: false, reason: data?.reason || "unknown" });
        setReactivateResult(result);
        if (result.ok) {
          // Refresh profile so deleted_at is cleared
          supabase.auth.getSession().then(({ data: sd }) => {
            if (sd?.session) {
              supabase.from("profiles").select("*").eq("id", sd.session.user.id).single()
                .then(({ data: p }) => { if (p) setSupabaseProfile(p); });
            }
          });
          // Fire reactivation confirmation email
          supabase.auth.getSession().then(({ data: sd }) => {
            if (sd?.session) {
              supabase.functions.invoke("process-account-deletion", {
                body: { action: "reactivated", user_id: sd.session.user.id },
                headers: { Authorization: `Bearer ${sd.session.access_token}` },
              }).catch(() => {});
            }
          });
        }
      })
      .catch(() => setReactivateResult({ ok: false, reason: "rpc_error" }))
      .finally(() => setReactivating(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── ?token= URL handler — email verification deep link ──
  useEffect(() => {
    // A team invite link also carries ?token= (a different token): the join page owns it.
    if (isTeamJoinPath(window.location.pathname)) return;
    const verifyToken = new URLSearchParams(window.location.search).get("token");
    if (!verifyToken) return;
    // Strip from URL immediately
    window.history.replaceState({}, "", window.location.pathname);
    setTokenVerifying(true);
    supabase.rpc("verify_email_token", { p_token: verifyToken })
      .then(({ data, error }) => {
        if (error || !data?.ok) {
          setTokenResult({ ok: false, reason: error ? "rpc_error" : (data?.reason || "unknown") });
          return;
        }
        // The token is verified server-side against ITS OWN account (data.user_id).
        // Only act on / display that account if it matches the session logged in
        // to THIS browser. If a DIFFERENT user is signed in here (or no one is),
        // we must not refresh their profile or fire their welcome/admin emails,
        // and we must not show them a "you're verified, continue" state for an
        // account that isn't theirs — that's the cross-user confusion bug.
        const verifiedUid = data.user_id || null;
        supabase.auth.getSession().then(({ data: sd }) => {
          const sessionUid = sd?.session?.user?.id || null;
          const action = verifyLandingAction({ verifiedUid, sessionUid });
          if (action === "continue") {
            // Normal path: the just-verified account is the one logged in here.
            setTokenResult({ ok: true });
            supabase
              .from("profiles")
              .select("*")
              .eq("id", sessionUid)
              .single()
              .then(({ data: p }) => { if (p) setSupabaseProfile(p); });
            // The welcome email is sent by the server when the account becomes verified (profiles trigger
            // on_profile_email_verified_welcome); the activation effect (lib/accountActivation.js) is the fallback.
          } else {
            // Not logged in here as the verified account: often a link from the Facebook or Instagram app that
            // opened in Safari or Chrome. Verified server-side (welcome included); open the login screen with a
            // "verified, log in to finish" note instead of leaving them on the home page. A different account
            // logged in here is signed out first.
            if (action === "switch_account") signOut().catch(() => {});
            setAuthNotice('alerts.emailVerifiedLogin');
            setAuthInitialMode("login");
            setView("auth");
            trackVerifyLoginPrompt(); // sign-up funnel: asked to log in again after verifying
          }
        });
      })
      .catch(() => setTokenResult({ ok: false, reason: "rpc_error" }))
      .finally(() => setTokenVerifying(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Share-link boot: ?share=<caseId> → public preview landing page ──
  // We always route to the dedicated share-preview surface (regardless of
  // auth state or role) so a shared link has one consistent destination
  // for every audience — guest, therapist, agency-self, admin. CaseViewer
  // renders the case inline in `preview` mode on that page; the page's
  // CTAs route signed-in users into the app at their role-appropriate
  // view, and unauthenticated users into the sign-in flow.
  //
  // sharedCase stays null while loadCaseById is in flight; sharedCaseLoading tells SharePreviewPage to show a
  // loading card then, not its "no longer available" state (that used to flash on every homepage case tap, owner
  // 2026-10-29). We still flip the view immediately so the recipient sees the brand header right away.
  const [sharedCase, setSharedCase] = useState(null);
  const [sharedCaseLoading, setSharedCaseLoading] = useState(false);
  // Set when this page load came from a /share/<id> link, consumed ONCE by the
  // session-routing effect below. Without it that effect overwrites the share
  // preview with the role default and the shared case is never seen — see the
  // note where it is consumed.
  const shareLinkRef = useRef(false);
  useEffect(() => {
    // Detect share links in two formats:
    //   /?share=<id>   — SPA target after Netlify edge fn redirect + backward compat
    //   /share/<id>    — canonical OG URL; handled by edge fn in prod, path fallback in local dev
    const paramId = new URLSearchParams(window.location.search).get("share");
    const pathId  = window.location.pathname.match(/^\/share\/([0-9a-f-]{36})$/i)?.[1];
    const shareId = paramId || pathId;
    //   /c/<CODE>      — short case link (abacases.com/c/RBT-1495). Resolved to the
    //                    case id below, then the identical share-preview flow.
    const shortCode = shareId ? null : shortLinkCode(window.location.pathname);
    if (!shareId && !shortCode) return;
    shareLinkRef.current = true;
    // Normalize URL to / so the share param doesn't persist on refresh.
    // NOTE: this is exactly why shareLinkRef exists — once the path is "/",
    // viewFromPath() returns null, so the session-routing effect can no longer
    // tell this was a share link and would fall through to the role default.
    window.history.replaceState({}, "", "/");
    _setView("share-preview");
    setSharedCaseLoading(true);
    // The /share path is unchanged. A short link first turns the code into the
    // case id (null for unknown / closed / hidden), then loads it the same way.
    (shareId ? loadCaseById(shareId) : resolveOpenCaseIdByCode(shortCode).then(id => (id ? loadCaseById(id) : null))).then(c => {
      if (c) setSharedCase(c);
      // null → SharePreviewPage's not-found state handles the message
    }).catch(() => {}).finally(() => setSharedCaseLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("calendar") !== "1") return;
    setInitialShowCalendar(true);
    // Strip the param so it doesn't re-trigger on refresh/back-nav, same as
    // the share-link and reactivate handlers above.
    const url = new URL(window.location.href);
    url.searchParams.delete("calendar");
    window.history.replaceState({}, "", url.pathname + url.search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onboardingTriggered = useRef(false);
  const [inboxThreadId, setInboxThreadId]      = useState(null);
  const [inboxOpen, setInboxOpen]              = useState(false);
  const [initialApplicationId, setInitialApplicationId] = useState(null);
  // ?calendar=1 URL deep link (e.g. the "Confirm interview" email CTA) —
  // opens My Applications straight into the Interview Calendar toggle.
  // Read once on boot below, alongside the other query-param handlers.
  const [initialShowCalendar, setInitialShowCalendar] = useState(false);
  const [initialApplicantDeepLink, setInitialApplicantDeepLink] = useState(null); // { applicationId, caseId }
  // Direct Invite — when an agency-invite notification is clicked the
  // matches view receives this caseId so the corresponding card can be
  // highlighted + scrolled into view. RBTProfile clears it on its own
  // timer; App.js just hands it through.
  const [highlightMatchCaseId, setHighlightMatchCaseId] = useState(null);
  const [inboxWidth, setInboxWidth]            = useState(Math.round(window.innerWidth * 0.5));

  const userId     = user?.id ?? null;

  // Team logins: who this agency account is to its agency (owner / member). Agency
  // screens work on agencyId, which is the owner's id for an active teammate. The
  // server decides the same thing (acting_agency_id); this only drives the screens.
  const [agencyCtx, setAgencyCtx] = useState(null);
  useEffect(() => {
    if (!userId || userRole !== "agency") { setAgencyCtx(null); return; }
    let live = true;
    loadAgencyContext().then((ctx) => { if (live) setAgencyCtx(ctx); });
    return () => { live = false; };
  }, [userId, userRole]);
  const teamMember = isTeamMember(agencyCtx);
  teamMemberRef.current = teamMember;
  // "In the app" signal for everyone signed in (lib/activityPing): holds unread message emails while they are here,
  // and keeps a teammate's "last active" on the Team screen fresh.
  useEffect(() => (userId ? startActivityPing() : undefined), [userId]);
  // Agency screens wait for the team context so a teammate never flashes their own empty account.
  const agencyReady = userRole !== "agency" || agencyCtx !== null;

  // "View application" email button (/dashboard?application=&case=, lib/applicantDeepLink): opens that applicant in
  // the agency pipeline once an agency is signed in and ready. Any other signed-in role just drops the link.
  useEffect(() => { captureApplicantLink(); }, []);
  useEffect(() => {
    if (!userRole || userRole === "guest" || !agencyReady) return;
    const link = takeApplicantLink();
    if (!link || userRole !== "agency") return;
    setInitialApplicantDeepLink(link);
    setView("dashboard");
  }, [userRole, agencyReady, setView]);
  const agencyId   = actingAgencyId(agencyCtx, userId);
  // A teammate's own full_name is a person, not the agency.
  const agencyName = teamMember ? (agencyCtx?.agencyName || "") : (supabaseProfile?.full_name || "");

  // Read what the agency already saved (NPI stamp is persisted server-side the moment it
  // succeeds) so the onboarding modal opens at the first incomplete step. A failed read
  // falls back to a fresh step 1, which is exactly the previous behavior.
  // The state is tagged with the user it was read for and only used when it matches the
  // signed-in user, so a sign-out / sign-in on a shared device can never open one agency's
  // setup prefilled with another agency's details. A read that hangs falls back to a
  // fresh step 1 after 5s so the modal can never be held back indefinitely.
  useEffect(() => {
    if (!showOnboarding || userRole !== "agency" || !userId) return;
    let cancelled = false;
    const settle = (profile) => {
      if (cancelled) return;
      cancelled = true;
      setAgencyResumeState({ userId, ...agencyResume(profile) });
    };
    const timer = setTimeout(() => settle(null), 5000);
    loadAgencyProfile(userId, { forceRefresh: true })
      .then(d => settle(d?.profile))
      .catch(() => settle(null));
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showOnboarding, userRole, userId]);
  const agencyResumeReady = agencyResumeState && agencyResumeState.userId === userId ? agencyResumeState : null;

  // ── One-time discovery tip: point therapists at the free Pay & Hours tracker.
  // Fires once per therapist (localStorage + dedupe_key guarded); skipped for
  // anyone who already uses the tracker. Best-effort; never blocks the app.
  useEffect(() => {
    if (!userId || !isTherapistRole(userRole) || !supabaseProfile?.onboarded) return;
    maybeNotifyTrackerDiscovery(userId, {
      title: t("tracker.discovery.title"),
      body: t("tracker.discovery.body"),
      // Suppresses the tip during the account's first day — see
      // trackerDiscovery.js's header. Prefer the profile row's timestamp and
      // fall back to the auth user's; either is the account's real birthday.
      accountCreatedAt: supabaseProfile?.created_at ?? user?.created_at ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userRole, supabaseProfile?.onboarded, supabaseProfile?.created_at]);

  // ── Global error capture ──
  useEffect(() => {
    const handleError = (event) => {
      captureError(
        { message: event.message, stack: event.error?.stack || "" },
        "window.onerror",
        userId
      );
    };
    const handleUnhandled = (event) => {
      captureError(
        { message: event.reason?.message || String(event.reason), stack: event.reason?.stack || "" },
        "unhandledrejection",
        userId
      );
    };
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandled);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandled);
    };
  }, [userId]);

  // ── When Supabase session appears (real login) ──
  useEffect(() => {
    if (authLoading) return;
    // /interview-respond is a standalone signed-link surface rendered below,
    // ahead of every gate. It is not a `view`, so this effect must not treat it
    // as an unknown deep link and rewrite the URL to /home — that would throw
    // away the signed params the page needs.
    if (isInterviewRespondPath(window.location.pathname)) return;
    // Same reasoning, same standalone surface, for /case-respond — see the
    // early-return render below and its comment.
    if (isCaseRespondPath(window.location.pathname)) return;
    // Same reasoning for /unsubscribe — a standalone token-link surface, not a `view`.
    if (isUnsubscribePath(window.location.pathname)) return;
    // And for /team-join, where a team invite email lands.
    if (isTeamJoinPath(window.location.pathname)) return;

    // A /share/<id> link has already selected the share preview, and SharePreviewPage
    // is meant to render for EVERY audience — guest, therapist, agency, admin.
    // The share effect normalizes the URL to "/", so by the time this runs
    // viewFromPath() is null and the logic below would resolve the role default
    // ("home" for a guest) and stomp the preview. That is why a shared case link
    // has landed logged-out visitors on the marketing home ever since per-case OG
    // metadata shipped: the case loads fine, it is simply overwritten.
    //
    // Consumed ONCE, deliberately. Signing in from the preview's "Sign in to
    // apply" CTA must still route the user to their role view rather than
    // stranding them on the preview forever.
    if (shareLinkRef.current) { shareLinkRef.current = false; return; }

    let currentPathView = viewFromPath();
    if (currentPathView && PAGE_ALIASES[currentPathView]) currentPathView = PAGE_ALIASES[currentPathView];

    if (session && supabaseProfile) {
      // A missing role usually means an incomplete/corrupt profile row. Default
      // to the lowest-privilege role (rbt) so we never silently escalate, but
      // log it so the data issue is visible rather than masked.
      if (!supabaseProfile.role) {
        console.warn("[App] profile has no role; defaulting to rbt for user", supabaseProfile.id);
      }
      const role = supabaseProfile.role || "rbt";
      setUserRole(role);
      // Merge the base profiles row into any existing profile for the same user
      // instead of replacing it. Supabase re-fires auth events (TOKEN_REFRESHED /
      // SIGNED_IN) when the tab regains focus, which re-runs this effect; replacing
      // the object wholesale would drop the already-merged therapist_profiles /
      // agency_profiles fields (skills, availability, photo, logo) and cause a
      // visible "incomplete profile" flash until the async re-merge below resolves.
      setUserProfile(prev =>
        prev && prev.id === supabaseProfile.id
          ? { ...prev, ...supabaseProfile }
          : supabaseProfile
      );

      // For onboarded therapists, also load therapist_profiles so isProfileComplete
      // works correctly and Best Matches is not incorrectly locked after login/refresh
      if (supabaseProfile.onboarded && isTherapistRole(role)) {
        loadTherapistProfile(supabaseProfile.id)
          .then(tProfile => {
            if (tProfile) setUserProfile(prev => ({ ...prev, ...tProfile }));
          })
          .catch(() => {});
      }
      if (supabaseProfile.onboarded && role === "agency") {
        loadAgencyProfile(supabaseProfile.id)
          .then(data => {
            if (data?.profile) setUserProfile(prev => ({ ...prev, logoUrl: data.profile.logoUrl, name: data.profile.name || prev.name }));
          })
          .catch(() => {});
      }

      // ── Hard gate: agency suspended ──
      // Suspended agencies see a full-screen message and cannot access the platform.
      // Admin is always exempt.
      if (supabaseProfile.suspended && role !== "admin") {
        _setView("agency-suspended");
        return;
      }

      // ── Hard gate: account pending deletion ──
      // If the user has a pending deletion, show the deletion pending screen.
      // Admin is exempt — they need full access to manage the platform.
      if (supabaseProfile.deleted_at && role !== "admin") {
        _setView("account-deleted"); // virtual view — renders AccountDeletionPending
        return;
      }

      // ── Hard gate: email verification required ──
      // Controlled exclusively by platform_config.require_email_verification.
      // mailer_autoconfirm is always true so email_confirmed_at is NOT used as a signal —
      // it is set immediately on signup and would bypass the gate for everyone.
      // The sole source of truth is profiles.email_verified.
      if (role !== "admin" && supabaseProfile.email_verified === false) {
        if (requireEmailVerification) {
          _setView("email-verify"); // virtual view — renders EmailVerificationPending
          return;
        } else {
          // Verification is OFF — auto-stamp so the user is never retroactively
          // blocked if the admin later enables the setting.
          supabase
            .from("profiles")
            .update({ email_verified: true })
            .eq("id", supabaseProfile.id)
            .then(() => setSupabaseProfile(prev => ({ ...prev, email_verified: true })))
            .catch(() => {});
          // Welcome + admin signup alert: the account activation effect (lib/accountActivation.js).
          // Fall through immediately — do not block the user.
        }
      }

      if (!supabaseProfile.onboarded && role !== "admin" && !onboardingTriggered.current) {
        onboardingTriggered.current = true;
        setShowOnboarding(true);
      }

      // ── Hard gate: non-onboarded users MUST complete onboarding ──
      // Block all navigation until onboarded — force the onboarding modal.
      if (!supabaseProfile.onboarded && role !== "admin") {
        _setView("cases-v2"); // background view doesn't matter — modal covers it
        return;
      }

      // Decide target view — preserve the current URL if it's valid for this role.
      // This avoids refreshes on /profile, /dashboard, etc. getting stomped back to
      // a role default just because the app re-booted.
      const allowed = ROLE_VIEWS[role] || new Set();
      let requested = initialUrlView.current;
      if (requested && PAGE_ALIASES[requested]) requested = PAGE_ALIASES[requested];
      initialUrlView.current = null; // consume once — subsequent navigations use pushState

      // A therapist who signed up from a tracker CTA lands straight in Pay & Hours
      // (consumed once — survives the onboarding gate via localStorage).
      const intent = consumeSignupIntent();
      // A guest who tapped Apply on a case before signing up (lib/signupIntent pending case): once onboarded, reopen
      // that case in Find Cases with the application form in view. Consumed once; other roles just drop it.
      const pendingCase = peekPendingCase();
      if (pendingCase) clearPendingCase();
      const resumeCase = pendingCase && isTherapistRole(role) && allowed.has("cases-v2") ? pendingCase : null;
      if (resumeCase) {
        // Closed, filled or hidden while they signed up: say so instead of dropping them on the map without a word.
        loadCaseById(resumeCase.id)
          .then((c) => {
            if (c) { setSharedCase({ ...c, _resumeApply: resumeCase.action === "apply" }); return; }
            setResumeMissed(resumeCase.code || "");
            trackApplyResumeMissed({ userId: session?.user?.id || null, pendingCase: resumeCase });
          })
          .catch(() => {});
      }

      let targetView;
      if (resumeCase)                                      targetView = "cases-v2";
      else if (currentPathView && allowed.has(currentPathView)) targetView = currentPathView;
      else if (requested && allowed.has(requested))        targetView = requested;
      else if (intent === "tracker" && allowed.has("tracker")) targetView = "tracker";
      else if (role === "agency")                         targetView = "dashboard";
      else if (role === "admin")                          targetView = "admin";
      else                                                 targetView = "cases-v2";

      _setView(targetView);
      if (!samePath(window.location.pathname, pathForView(targetView))) {
        window.history.replaceState({ view: targetView }, "", pathForView(targetView));
      }

    } else if (!session) {
      setUserRole("guest");
      setUserProfile(null);

      // Respect guest-accessible deep links, otherwise land on the SEO home.
      let requested = initialUrlView.current;
      if (requested && PAGE_ALIASES[requested]) requested = PAGE_ALIASES[requested];
      initialUrlView.current = null;

      // Spanish SEO landing: /es is a crawlable, independently-indexable Spanish
      // twin of the home (listed in the sitemap with hreflang="es"). Force the
      // language and render the home; HomeV2 self-canonicalizes to /es on this
      // path rather than folding into the English /. The rest of the app is
      // untouched (language otherwise resolved by cookie/navigator detection).
      const isEs = currentPathView === "es";
      if (isEs) { try { i18n.changeLanguage("es"); } catch { /* ignore */ } }

      let targetView = isEs ? "home"
        : currentPathView && ROLE_VIEWS.guest.has(currentPathView)
        ? currentPathView
        : requested && ROLE_VIEWS.guest.has(requested)
          ? requested
          : "home";

      // Returning-visitor fast path: anyone who's been here before skips the
      // marketing home and lands straight on the case map. Bots and first-time
      // visitors still get the SEO home (crawlers don't persist localStorage).
      // Only applies to a plain home landing — explicit deep links are honored.
      let visited = false;
      try { visited = !!localStorage.getItem("ac_visited"); } catch { /* storage blocked */ }
      // Only fast-path to the map when there is actually a map to show. While
      // public browsing is gated a returning guest keeps the marketing home,
      // which is where the signup/login decision belongs — otherwise every
      // repeat visit would land on the gate.
      // This branch is the logged-out path, so the visitor is a guest by
      // definition — public browsing being ON is the only thing that makes the
      // map worth fast-pathing to.
      if (targetView === "home" && visited && publicCaseBrowsingRef.current) targetView = "cases-v2";
      try { localStorage.setItem("ac_visited", "1"); } catch { /* ignore */ }

      _setView(targetView);
      const url = pathForView(targetView, { spanishHome: isEs });
      if (!samePath(window.location.pathname + window.location.hash, url)) {
        window.history.replaceState({ view: targetView }, "", url);
      }
    }
  }, [session, supabaseProfile, authLoading, requireEmailVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Account activation: welcome email + admin signup alert ──
  // Fires on any device once a signed-in account is active (verified, or verification off), until the server
  // records them as sent. One run per account per page load; a failure is logged and retried on the next load.
  const activationRan = useRef(null);
  useEffect(() => {
    if (authLoading || !session?.access_token || !supabaseProfile?.id) return;
    if (activationRan.current === supabaseProfile.id) return;
    const due = activationCallsDue(supabaseProfile, { requireEmailVerification });
    if (!due.welcome && !due.adminNotify) return;
    activationRan.current = supabaseProfile.id;
    runActivationCalls(due, session.access_token, i18n.language?.startsWith("es") ? "es" : "en");
  }, [session, supabaseProfile, authLoading, requireEmailVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legal-acceptance check ──
  // After a user has a session + a profile (so we know they're not a guest),
  // verify they've accepted the current ToS and PP versions. If not, raise
  // the gate. Admins are exempted because they manage the platform — they
  // already implicitly bind themselves by virtue of operating it.
  useEffect(() => {
    if (!session || !supabaseProfile || authLoading) {
      setNeedsLegalAcceptance(null);
      return;
    }
    if (supabaseProfile.role === "admin") {
      setNeedsLegalAcceptance(false);
      return;
    }
    let cancelled = false;
    getAcceptanceStatus(supabaseProfile.id)
      .then((status) => {
        if (cancelled) return;
        setNeedsLegalAcceptance(!status.tosCurrent || !status.ppCurrent);
      })
      .catch(() => { if (!cancelled) setNeedsLegalAcceptance(false); }); // soft-fail open — don't lock out on transient errors
    return () => { cancelled = true; };
    // Depend on stable primitives, NOT the supabaseProfile object — its
    // identity changes on every profile-state update, which previously
    // re-fired getAcceptanceStatus() (2 queries to tos/pp tables) in bursts.
  }, [session?.user?.id, supabaseProfile?.id, supabaseProfile?.role, authLoading]);

  // ── Sign out ──
  async function handleSignOut() {
    await signOut();
  }

  // ── One-tap interview response (signed email link) ──
  // Mounted before EVERY gate — auth loading, recovery, suspension, email
  // verification, legal acceptance — because this page must render for someone
  // with no session at all, straight from their inbox, on a phone. Waiting on
  // auth would also mean a spinner for a page that needs none of it: the only
  // authorization here is the HMAC in the URL, and only the server judges that.
  // Deliberately keyed off the pathname rather than a `view`, so nothing in the
  // role/view routing can redirect it to /auth or /home.
  if (isInterviewRespondPath(window.location.pathname)) {
    return (
      <ErrorBoundary userId={null}>
        <InterviewRespondPage />
      </ErrorBoundary>
    );
  }

  // ── One-click case-expiry response (signed email link) ──
  // Same reasoning as /interview-respond above: mounted before EVERY gate
  // because it must render for someone with no session, straight from their
  // inbox, on a phone. Keyed off the pathname rather than a `view`, so
  // nothing in the role/view routing can redirect it to /auth or /home.
  if (isCaseRespondPath(window.location.pathname)) {
    return (
      <ErrorBoundary userId={null}>
        <CaseRespondPage />
      </ErrorBoundary>
    );
  }

  // ── Cold-outreach unsubscribe (one-click link) ──
  // Same reasoning as interview-respond above: mounted before every gate so it
  // renders for someone with no session, straight from their inbox.
  if (isUnsubscribePath(window.location.pathname)) {
    return (
      <ErrorBoundary userId={null}>
        <UnsubscribePage />
      </ErrorBoundary>
    );
  }

  if (authLoading || tokenVerifying || reactivating) {
    return (
      <div style={{
        height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Inter', sans-serif", background: T.bg, color: T.textMid, fontSize: 14,
      }}>
        {tokenVerifying ? t('alerts.verifyingEmail') : reactivating ? t('alerts.reactivatingAccount') : t('common.loading')}
      </div>
    );
  }

  // ── Team invite (emailed link) ──
  // After auth loading (it must know whether someone is already signed in) but
  // before every other gate: the invited teammate has no account yet.
  if (isTeamJoinPath(window.location.pathname)) {
    return (
      <ErrorBoundary userId={userId}>
        <TeamJoinPage session={session} onSignOut={handleSignOut} />
      </ErrorBoundary>
    );
  }

  // Password recovery — user arrived via a reset-password email link
  if (recoveryMode) {
    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: T.bg }}>
        <ResetPasswordScreen onDone={() => setRecoveryMode(false)} />
      </div>
    );
  }

  // Team logins: a teammate whose access is paused (agency not on Pro) or removed.
  if (session && userRole === "agency" && teamMember && agencyCtx.status !== "active") {
    return <TeamAccessScreen context={agencyCtx} onSignOut={handleSignOut} />;
  }

  // Agency suspended — full-screen gate
  if (view === "agency-suspended" && session) {
    return <SuspendedAgencyScreen profile={supabaseProfile} onSignOut={handleSignOut} />;
  }

  // Account deletion pending — full-screen gate
  if (view === "account-deleted" && session) {
    return (
      <AccountDeletionPending
        profile={supabaseProfile}
        onReactivated={() => {
          // Refresh profile — deleted_at will be null after reactivation
          supabase.from("profiles").select("*").eq("id", user.id).single()
            .then(({ data: p }) => { if (p) setSupabaseProfile(p); });
        }}
        onSignOut={handleSignOut}
      />
    );
  }

  // Email verification pending — full-screen gate.
  // We trip this directly from the profile flag, not only from `view`: on a
  // hard refresh the persisted view (e.g. "cases-v2") renders for one frame
  // before the routing effect redirects to "email-verify". That one frame is
  // enough to mount Leaflet and then crash it on the immediate unmount (a zoom
  // transitionend firing on a torn-down map pane). Guarding at render time
  // keeps the map from ever mounting for an unverified user.
  const emailGatePending =
    session && supabaseProfile && userRole !== "admin" &&
    supabaseProfile.email_verified === false && requireEmailVerification;
  if ((view === "email-verify" && session) || emailGatePending) {
    return (
      <EmailVerificationPending
        email={user?.email || ""}
        onSignOut={handleSignOut}
      />
    );
  }

  // Legal acceptance gate — full-screen, blocks UI until current ToS+PP accepted.
  // Mounted after the special-state gates so we don't double-render with them.
  // Onboarding overlay coexists with this gate — onboarding may proceed but we
  // also stamp the user's acceptance before they can do anything else.
  if (session && supabaseProfile && needsLegalAcceptance === true) {
    return (
      <AcceptanceGate
        userId={supabaseProfile.id}
        userEmail={supabaseProfile.email}
        onAccepted={() => setNeedsLegalAcceptance(false)}
        onSignOut={handleSignOut}
      />
    );
  }

  // Reactivation result banner
  const reactivateBanner = reactivateResult && (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: reactivateResult.ok ? T.green : T.red,
      color: "#fff", textAlign: "center",
      padding: "12px 16px", fontSize: 14, fontWeight: 600,
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }}>
      {reactivateResult.ok
        ? `✓ ${t('alerts.reactivationSuccess')}`
        : reactivateResult.reason === "token_expired"
          ? t('alerts.reactivationExpired')
          : reactivateResult.reason === "not_deleted"
            ? t('alerts.reactivationAlreadyActive')
            : t('alerts.reactivationInvalid')}
      <button
        onClick={() => setReactivateResult(null)}
        style={{ marginLeft: 16, background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 4, color: "#fff", cursor: "pointer", padding: "2px 8px", fontSize: 13 }}
      >✕</button>
    </div>
  );

  // Token result feedback banner (shown inline on top of normal UI)
  const tokenBanner = tokenResult && (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: tokenResult.ok ? T.green : T.red,
      color: "#fff", textAlign: "center",
      padding: "12px 16px", fontSize: 14, fontWeight: 600,
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }}>
      {tokenResult.ok
        ? `✓ ${t('alerts.emailVerifiedSuccess')}`
        : tokenResult.reason === "already_used"
          ? t('alerts.emailVerificationUsed')
          : tokenResult.reason === "expired"
            ? t('alerts.emailVerificationExpired')
            : t('alerts.emailVerificationInvalid')}
      <button
        onClick={() => setTokenResult(null)}
        style={{ marginLeft: 16, background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 4, color: "#fff", cursor: "pointer", padding: "2px 8px", fontSize: 13 }}
      >✕</button>
    </div>
  );

  const resumeMissedBanner = resumeMissed !== null && (
    <div data-testid="resume-missed" role="status" style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: T.text, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      padding: "6px 8px 6px 16px", fontSize: 14, fontWeight: 600, lineHeight: 1.4,
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }}>
      <span>{resumeMissed ? t('alerts.resumeMissed', { code: resumeMissed }) : t('alerts.resumeMissedGeneric')}</span>
      <button
        onClick={() => setResumeMissed(null)}
        aria-label={t('common.close')}
        style={{ minWidth: 44, minHeight: 44, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, flexShrink: 0 }}
      >✕</button>
    </div>
  );

  return (
    <ErrorBoundary userId={userId}>
    {reactivateBanner}
    {tokenBanner}
    {resumeMissedBanner}
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: isMobile ? "auto" : "100vh",
      minHeight: "100vh",
      overflow: isMobile ? "visible" : "hidden",
      fontFamily: "'Inter', 'DM Sans', sans-serif",
      background: T.bg,
      // Offset for fixed TopNav (56px) and bottom tab bar on mobile, including safe area.
      paddingTop: (view !== "auth" && view !== "terms-of-use" && view !== "privacy-policy" && view !== "retention" && view !== "support" && view !== "share-preview") ? 56 : 0,
      // Only therapist roles get the fixed bottom tab bar (TopNav getBottomTabs); guests, agencies and admins have none,
      // so reserving 88px for them left an empty grey (T.bg) band under the public footer on phones.
      paddingBottom: isMobile && isTherapistRole(userRole) ? "calc(88px + env(safe-area-inset-bottom, 0px))" : 0,
    }}>
      {ownerOnlyAsk && <OwnerOnlyDialog onClose={() => setOwnerOnlyAsk(false)} />}
      {view !== "auth" && view !== "terms-of-use" && view !== "privacy-policy" && view !== "retention" && view !== "support" && view !== "share-preview" && (
        <TopNav
          view={view}
          setView={(v) => {
            if (v === "inbox" && !isMobile) { setInboxOpen(true); return; }
            setView(v);
          }}
          userRole={userRole}
          userName={userProfile?.name || userProfile?.full_name || supabaseProfile?.full_name || ""}
          userPhotoUrl={userProfile?.photoUrl || userProfile?.logoUrl || ""}
          showJobAlerts={showJobAlerts}
          setShowJobAlerts={setShowJobAlerts}
          alertInitialFilters={alertInitialFilters}
          onJobAlertsClose={() => { setShowJobAlerts(false); setAlertInitialFilters(null); }}
          isProfileComplete={isProfileComplete(userProfile)}
          onSignOut={handleSignOut}
          userId={userId}
          agencyId={userRole === "agency" ? agencyId : null}
          teamMember={teamMember}
          agencyPro={agencyCtx?.isPro === true}
          userEmail={supabaseProfile?.email || ""}
          isTestAccount={supabaseProfile?.is_test === true}
          onAuth={(mode) => {
            // Top-nav "Sign up free": on an agency page it means an agency signup; elsewhere, drop a stale agency intent
            if (mode === "signup") {
              if (AGENCY_PAGE_VIEWS.has(view)) setSignupIntent("agency");
              else if (peekSignupIntent() === "agency") clearSignupIntent();
            }
            setAuthInitialMode(mode === "signup" ? "signup" : "login"); setView("auth");
          }}
          onOpenInboxThread={(applicationId) => {
            setInboxThreadId(applicationId || null);
            if (isMobile) { setView("inbox"); } else { setInboxOpen(true); }
          }}
          onOpenApplicationThread={(applicationId) => {
            setInitialApplicationId(applicationId || null);
            setView("applications");
          }}
          onOpenApplicantCard={({ applicationId, caseId }) => {
            setInitialApplicantDeepLink({ applicationId, caseId });
            setView("dashboard");
          }}
          onHighlightMatchCase={(caseId) => {
            setHighlightMatchCaseId(caseId || null);
            setView("matches");
          }}
        />
      )}

      {/* One boundary for the lazy signed-in screens. The fallback is an empty flex filler (same footprint a page takes), so nothing shifts when a screen arrives. */}
      <Suspense fallback={<div aria-hidden="true" style={{ flex: 1, minHeight: "60vh" }} />}>
      {/* Live homepage — therapist-first HomeV2 (canonical /, indexable). */}
      {view === "home"      && userRole === "guest" && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <HomeV2 onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
        </div>
      )}
      {/* Free ABA Timesheet & Payment Tracker — SEO landing (/pay-tracker 301s here). */}
      {view === "free-aba-timesheet-payment-tracker" && userRole === "guest" && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <PayTrackerLanding
            onSignUp={() => { setAuthInitialMode("signup"); setView("auth"); }}
          />
        </div>
      )}
      {/* ABA Case Finder — standalone SEO landing (TherapistLanding, not embedded). */}
      {view === "aba-case-finder" && userRole === "guest" && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <TherapistLanding
            onLearnMoreTracker={() => setView("free-aba-timesheet-payment-tracker")}
            onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }}
          />
        </div>
      )}
      {/* ABA Agencies — standalone SEO landing (AgencyLanding, not embedded). */}
      {view === "aba-agencies" && userRole === "guest" && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <AgencyLanding onSignUp={() => { track(A.AGENCY_POST_CASE_CLICKED, { supabaseMeta: { source: "aba_agencies" } }); setSignupIntent("agency"); setAuthInitialMode("signup"); setView("auth"); }} />
        </div>
      )}
      {/* Role × city SEO landing pages (e.g. /rbt-jobs-miami-fl) — data-driven. */}
      {SEO_LANDING_SLUGS.includes(view) && userRole === "guest" && (
        <CityRolePage
          slug={view}
          onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }}
        />
      )}
      {/* About ABAcases — brand-authority SEO page (Phase 2B). */}
      {view === "about-aba-cases" && userRole === "guest" && (
        <AboutAbaCases onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {/* ABA Case Finder Alternative — brand/category SEO page (Phase 2B). */}
      {view === "aba-case-finder-alternative" && userRole === "guest" && (
        <AbaCaseFinderAlternative onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {/* Tracker keyword cluster — SEO pages (Phase 2C). */}
      {view === "aba-timesheet" && userRole === "guest" && (
        <AbaTimesheet onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "rbt-timesheet" && userRole === "guest" && (
        <RbtTimesheet onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "aba-payment-tracker" && userRole === "guest" && (
        <AbaPaymentTracker onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {/* Agency acquisition + provider demand capture — SEO pages (Phase 3A). */}
      {view === "post-aba-cases" && userRole === "guest" && (
        <PostAbaCases onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {/* Cold-outreach landing (noindex, not prerendered / not in the sitemap). */}
      {view === "post-a-case" && userRole === "guest" && (
        <PostACase onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}

      {view === "aba-staffing-for-small-agencies" && userRole === "guest" && (
        <AbaStaffingForSmallAgencies onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {/* Florida statewide agency staffing page (Phase 4B). */}
      {view === "aba-staffing-florida" && userRole === "guest" && (
        <AbaStaffingFlorida onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "aba-provider-waitlist" && userRole === "guest" && (
        <AbaProviderWaitlist onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "rbt-staffing-charlotte-nc" && userRole === "guest" && (
        <RbtStaffingCharlotteNc onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "rbt-staffing-miami-fl" && userRole === "guest" && (
        <RbtStaffingMiamiFl onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {/* Provider authority content cluster — SEO pages (Phase 3B). */}
      {view === "how-to-become-an-rbt" && userRole === "guest" && (
        <HowToBecomeAnRbt onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "rbt-resume-guide" && userRole === "guest" && (
        <RbtResumeGuide onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "rbt-interview-questions" && userRole === "guest" && (
        <RbtInterviewQuestions onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "how-to-choose-an-aba-case" && userRole === "guest" && (
        <HowToChooseAnAbaCase onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "travel-rbt-jobs" && userRole === "guest" && (
        <TravelRbtJobs onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "rbt-vs-bt-vs-bcba-vs-bcaba" && userRole === "guest" && (
        <RbtVsBtVsBcbaVsBcaba onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "aba-jobs-by-state" && userRole === "guest" && (
        <AbaJobsByState onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "aba-resources" && userRole === "guest" && (
        <AbaResourcesHub onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "how-to-become-a-bcba" && userRole === "guest" && (
        <HowToBecomeABcba onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "how-to-become-a-bcaba" && userRole === "guest" && (
        <HowToBecomeABcaba onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "aba-roles-guide" && userRole === "guest" && (
        <AbaRolesGuide onNavigate={(v) => { if (v === "auth") { setAuthInitialMode("signup"); } setView(v); }} />
      )}
      {view === "auth"      && <AuthScreen onNavigate={setView} initialMode={authInitialMode} notice={authNotice ? t(authNotice) : null} />}
      {view === "share-preview" && (
        <SharePreviewPage
          caseData={sharedCase}
          loading={sharedCaseLoading && !sharedCase}
          userRole={userRole}
          /* Browse-all routes signed-in users to their role's discovery
             surface; guests get the same therapist-facing discovery view
             they'd reach via the marketing landing. */
          onBrowseAll={() => {
            setSharedCase(null);
            if (userRole === "agency") setView("dashboard");
            else if (userRole === "admin") setView("admin");
            else setView("cases-v2");
          }}
          /* Primary CTA. For guests this routes to auth (sharedCase is
             preserved so the modal pops after sign-in). For signed-in
             users this routes them straight to cases-v2 where the
             existing sharedCase effect opens the case modal — matching
             pre-refactor behaviour for the already-signed-in path. */
          onLoginRequired={() => {
            if (userRole === "guest") {
              // Keep sharedCase populated — auth-success effect routes
              // therapists to cases-v2 where it pops as a modal. The case is
              // also remembered on the device (CaseViewer → signupIntent), so
              // it survives a reload; most of these visitors are new: sign up.
              setAuthInitialMode("signup");
              setView("auth");
            } else if (userRole === "agency") {
              setView("dashboard");
            } else if (userRole === "admin") {
              setView("admin");
            } else {
              setView("cases-v2");
            }
          }}
        />
      )}
      {view === "terms-of-use"   && (
        <LegalPage
          type="tos"
          onBack={v => v ? setView(v) : setView(userRole === "guest" ? "auth" : userRole === "agency" ? "dashboard" : "cases-v2")}
        />
      )}
      {view === "privacy-policy" && (
        <LegalPage
          type="pp"
          onBack={v => v ? setView(v) : setView(userRole === "guest" ? "auth" : userRole === "agency" ? "dashboard" : "cases-v2")}
        />
      )}
      {view === "retention"      && (
        <LegalPage
          type="drp"
          onBack={v => v ? setView(v) : setView(userRole === "guest" ? "auth" : userRole === "agency" ? "dashboard" : "cases-v2")}
        />
      )}
      {view === "support"        && (
        <SupportPage
          onBack={() => setView(userRole === "guest" ? "auth" : userRole === "agency" ? "dashboard" : "cases-v2")}
        />
      )}
      {view === "profile"   && (
        <RBTProfile
          userRole={userRole}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          userId={userId}
          onNavigate={setView}
        />
      )}
      {view === "matches"   && userRole === "guest" && (
        <BestMatchesTeaser onLoginRequired={(mode = "login") => { setAuthInitialMode(mode === "signup" ? "signup" : "login"); setView("auth"); }} />
      )}
      {view === "matches"   && userRole !== "guest" && (
        <RBTProfile
          userRole={userRole}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          userId={userId}
          matchesOnly
          onNavigate={setView}
          highlightCaseId={highlightMatchCaseId}
          onClearHighlight={() => setHighlightMatchCaseId(null)}
        />
      )}
      {view === "billing"      && userRole === "agency" && agencyReady && (
        canManageAgency(agencyCtx)
          ? <Billing userId={userId} />
          : <OwnerOnlyNotice context={agencyCtx} onBack={() => setView("dashboard")} />
      )}
      {view === "dashboard"    && userRole === "agency" && agencyReady && (
        <TeamNamesProvider enabled={agencyCtx?.role === "owner" || teamMember}>
        <AgencyDashboard
          userId={agencyId}
          viewerId={userId}
          teamContext={agencyCtx}
          agencyName={agencyName}
          onNavigate={setView}
          onOpenInbox={(applicationId) => {
            setInboxThreadId(applicationId || null);
            if (isMobile) {
              setView("inbox");
            } else {
              setInboxOpen(true);
            }
          }}
          initialApplicantDeepLink={initialApplicantDeepLink}
          onApplicantDeepLinkConsumed={() => setInitialApplicantDeepLink(null)}
        />
        </TeamNamesProvider>
      )}
      {view === "admin"        && userRole === "admin"  && <AdminPanel />}
      {view === "applications" && userRole === "guest" && (
        <MyApplicationsTeaser onLoginRequired={(mode = "login") => { setAuthInitialMode(mode === "signup" ? "signup" : "login"); setView("auth"); }} />
      )}
      {view === "applications" && isTherapistRole(userRole) && (
        <MyApplications
          userRole={userRole}
          userId={userId}
          onNavigate={setView}
          onOpenInbox={(applicationId) => { setInboxThreadId(applicationId || null); if (isMobile) { setView("inbox"); } else { setInboxOpen(true); } }}
          initialApplicationId={initialApplicationId}
          onApplicationFocusConsumed={() => setInitialApplicationId(null)}
          initialShowCalendar={initialShowCalendar}
          onCalendarFocusConsumed={() => setInitialShowCalendar(false)}
        />
      )}
      {view === "tracker"      && isTherapistRole(userRole) && (
        <Tracker userId={userId} userProfile={userProfile} userRole={userRole} onNavigate={setView} profileComplete={isProfileComplete(userProfile)} />
      )}
      {view === "agencyabout"  && userRole === "agency" && agencyReady && (
        canManageAgency(agencyCtx)
          ? <AgencyAbout userId={userId} onBack={() => setView("dashboard")} onProfileSaved={(updates) => setUserProfile(prev => ({ ...prev, ...updates }))} />
          : <OwnerOnlyNotice context={agencyCtx} onBack={() => setView("dashboard")} />
      )}
      {view === "team"         && userRole === "agency" && agencyReady && (
        canManageAgency(agencyCtx)
          ? <TeamPage isMobile={isMobile} onNavigate={setView} />
          : <OwnerOnlyNotice context={agencyCtx} onBack={() => setView("dashboard")} />
      )}
      {view === "inbox"        && ["rbt","bcba","bcaba","agency"].includes(userRole) && agencyReady && <Inbox userRole={userRole} userId={agencyId} viewerId={userId} initialThreadId={inboxThreadId} onNavigate={setView} onViewApplication={(applicationId, caseId) => { if (userRole === "agency") { setInitialApplicantDeepLink({ applicationId, caseId }); setView("dashboard"); } else { setInitialApplicationId(applicationId || null); setView("applications"); } }} userName={userProfile?.name || userProfile?.full_name || ""} userPhotoUrl={userProfile?.photoUrl || userProfile?.logoUrl || ""} />}
      {view === "notifications"&& ["rbt","bcba","bcaba","agency"].includes(userRole) && <NotificationPreferences userId={userId} userRole={userRole} />}
      {/* therapist-landing / agency-landing are retired view ids — the live nav
          uses /aba-case-finder and /aba-agencies; old links resolve straight to
          those pages (see PAGE_ALIASES). */}
      {view === "cases-v2" && userRole === "guest" && !publicCaseBrowsing && (
        <CaseFinderGate
          onSignUp={() => { setAuthInitialMode("signup"); setView("auth"); }}
          onLogin={() => { setAuthInitialMode("login"); setView("auth"); }}
          onTracker={() => setView("free-aba-timesheet-payment-tracker")}
        />
      )}

      {view === "cases-v2"     && ["rbt","bcba","bcaba","guest"].includes(userRole)
        && !(userRole === "guest" && !publicCaseBrowsing) && (
        <FindCasesV2
          userRole={userRole}
          userProfile={userProfile}
          userId={userId}
          onSetupAlerts={(filters) => { setAlertInitialFilters(filters || null); setShowJobAlerts(true); }}
          onLoginRequired={(mode = "login") => { setAuthInitialMode(mode); setView("auth"); }}
          onNavigate={setView}
          sharedCase={sharedCase}
          onSharedCaseConsumed={() => setSharedCase(null)}
        />
      )}

      {/* ── Desktop Inbox slide-in panel ── */}
      {inboxOpen && !isMobile && ["rbt","bcba","bcaba","agency"].includes(userRole) && (
        <div
          style={{
            position: "fixed", top: 56, left: 0, right: 0, bottom: 0, zIndex: 900,
            display: "flex", justifyContent: "flex-end",
          }}
        >
          {/* Backdrop */}
          <div
            onClick={() => setInboxOpen(false)}
            style={{
              position: "absolute", inset: 0,
              background: "rgba(15,23,42,0.25)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
            }}
          />
          {/* Resize handle */}
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              const startX = e.clientX;
              const startW = inboxWidth;
              const onMove = (ev) => setInboxWidth(Math.max(560, Math.min(window.innerWidth - 48, startW + startX - ev.clientX)));
              const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
              document.addEventListener("mousemove", onMove);
              document.addEventListener("mouseup", onUp);
            }}
            style={{
              width: 6, cursor: "col-resize", zIndex: 2,
              background: "transparent", position: "relative", flexShrink: 0,
            }}
          >
            <div style={{ position: "absolute", left: 2, top: "50%", transform: "translateY(-50%)", width: 3, height: 40, borderRadius: 2, background: "rgba(255,255,255,0.4)" }} />
          </div>
          {/* Panel */}
          <div style={{
            width: inboxWidth, maxWidth: "96vw", minWidth: 0,
            background: "#fff",
            boxShadow: "-8px 0 30px rgba(0,0,0,0.15)",
            display: "flex", flexDirection: "column",
            animation: "inboxSlideIn 0.2s ease-out",
            position: "relative", zIndex: 1,
          }}>
            <style>{`@keyframes inboxSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
              .inbox-chrome-btn { transition: background 0.12s, color 0.12s; }
              .inbox-chrome-btn:hover { background: rgba(0,0,0,0.10) !important; color: #1E1B4B !important; }`}</style>
            {/* Window chrome — one aligned cluster, top-right: expand/collapse then
                close. Clear icons (expand arrows ↔ collapse arrows) instead of the
                old ⤢/✕ glyphs. */}
            {(() => {
              const isMax = inboxWidth >= window.innerWidth * 0.78;
              return (
                <div style={{ position: "absolute", top: 16, right: 18, zIndex: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    className="inbox-chrome-btn"
                    onClick={() => setInboxWidth(isMax ? Math.round(window.innerWidth * 0.5) : window.innerWidth - 48)}
                    title={isMax ? "Collapse panel" : "Expand panel"}
                    aria-label={isMax ? "Collapse panel" : "Expand panel"}
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: "rgba(0,0,0,0.05)", border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B",
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {isMax
                        ? <><path d="M9 4v6H3M21 14h-6v6" /><path d="M10 9 3 2M14 15l7 7" /></>
                        : <><path d="M15 4h6v6M3 14v6h6" /><path d="M21 4l-7 7M3 21l7-7" /></>}
                    </svg>
                  </button>
                  <button
                    className="inbox-chrome-btn"
                    onClick={() => setInboxOpen(false)}
                    title="Close"
                    aria-label="Close inbox"
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: "rgba(0,0,0,0.05)", border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B",
                    }}
                  >
                    <XIcon size={16} />
                  </button>
                </div>
              );
            })()}
            <Suspense fallback={null}>
            <Inbox
              userRole={userRole}
              userId={agencyId}
              viewerId={userId}
              initialThreadId={inboxThreadId}
              onNavigate={(v) => { setInboxOpen(false); setView(v); }}
              onViewApplication={(applicationId, caseId) => {
                setInboxOpen(false);
                if (userRole === "agency") { setInitialApplicantDeepLink({ applicationId, caseId }); setView("dashboard"); }
                else { setInitialApplicationId(applicationId || null); setView("applications"); }
              }}
              userName={userProfile?.name || userProfile?.full_name || ""}
              userPhotoUrl={userProfile?.photoUrl || userProfile?.logoUrl || ""}
            />
            </Suspense>
          </div>
        </div>
      )}

      {showOnboarding && (userRole !== "agency" || agencyResumeReady) && (
        <Suspense fallback={null}>
        <Onboarding
          key={userId || "anon"}
          userRole={userRole}
          startStep={userRole === "agency" ? (agencyResumeReady?.startStep ?? 1) : 1}
          initialData={userRole === "agency" ? agencyResumeReady?.data : undefined}
          initialProfile={supabaseProfile || {}}
          onComplete={async (profileData) => {
            setUserProfile(profileData);
            // NOTE: the modal is NOT closed here. It used to be, optimistically,
            // before the save was even attempted — so any failure below (a bad
            // credentialed_date, a network blip, an RLS rejection) left the user
            // looking "done" locally while profiles.onboarded stayed false on
            // the server. That produced real accounts browsing the app in a
            // half-onboarded state with no name/ZIP, and the only trace was a
            // console.error nobody sees. The modal now closes only on success;
            // failures re-throw so Onboarding can keep it open and say so.
            if (userId) {
              try {
                if (userRole === "agency") {
                  await saveAgencyProfile(userId, {
                    name:         profileData.name,
                    dbaName:      profileData.dbaName,
                    zip:          profileData.zip,
                    city:         profileData.city,
                    state:        profileData.state,
                    about:        profileData.about,
                    email:        profileData.email,
                    phone:        profileData.phone,
                    npi:          profileData.npi,
                    npiVerified:  profileData.npiVerified || false,
                    npiOrgName:   profileData.npiOrgName || null,
                    settings:     profileData.settings,
                    languages:    profileData.languages,
                  }, { phone: !!profileData.phone, email: !!profileData.email, website: false });
                  // No plan is written here any more. This used to stamp
                  // localStorage with {plan: <chosen>, status: "active"} — for
                  // an agency who picked Pro that asserted an active paid
                  // subscription that did not exist anywhere: no Stripe
                  // customer, no subscription, no invoice, and profiles.plan
                  // still "starter". Everyone starts on Starter; the upgrade
                  // happens in Billing where the charge is real. The
                  // notification below points them there.
                  //
                  // Clear rather than merely stop writing: an agency who went
                  // through the OLD flow and picked Pro still has that false
                  // claim sitting in their browser, and CasePosting reads this
                  // key as its fallback when the posting-slot RPC fails — which
                  // would hand them Pro's listing cap. Billing still writes this
                  // key on real, server-backed plan changes; only the bogus
                  // onboarding-time value is being removed.
                  try { localStorage.removeItem("aba_subscription_plan"); } catch (_) {}
                } else {
                  // Onboarding may finish minimally (name + ZIP via "Finish
                  // later"); allow the incomplete save — the ZIP still geocodes
                  // them onto the map, and isProfileComplete() keeps the
                  // "complete your profile" nudges + locked Best Matches active.
                  await saveTherapistProfile(userId, profileData, { allowIncomplete: true });
                  // Best-effort: if a credentialed therapist opted into verification
                  // during onboarding, submit it for admin review now. BT is
                  // uncredentialed (credential === "BT") and is never submitted.
                  // A failure here must not block onboarding completion.
                  const cred = (profileData.credential || "").trim();
                  if (
                    userRole !== "agency" &&
                    cred &&
                    cred !== "BT" &&
                    profileData.verifyConsent === true &&
                    validateCredentialFormat(cred, userRole) === null &&
                    (profileData.expirationDate || "").trim()
                  ) {
                    try {
                      await requestVerification(userId, cred);
                      setUserProfile(p => ({ ...p, verificationStatus: "pending" }));
                    } catch (vErr) {
                      console.warn("Verification submission during onboarding failed:", vErr);
                    }
                  }
                }
                await markUserOnboarded(userId);
                setSupabaseProfile(p => ({ ...p, onboarded: true, full_name: profileData.name || p.full_name, email: profileData.email || p.email }));

                // Fire-and-forget: prompt new user to complete their profile
                const isAgencyRole = userRole === "agency";
                createNotification({
                  user_id:    userId,
                  type:       "system",
                  title:      isAgencyRole
                    ? t('notifications.finishAgencyProfile')
                    : t('notifications.completeProfileTitle'),
                  body:       isAgencyRole
                    ? t('notifications.finishAgencyProfileBody')
                    : t('notifications.completeProfileBody'),
                  // Same fix as above: this had an action_url, which is dropped
                  // on the way to the DB — so this notification has never
                  // navigated anywhere, it just marked itself read.
                  link_data:  { view: isAgencyRole ? "agencyabout" : "profile" },
                  dedupe_key: `onboarding_complete_${userId}`,
                  read:       false,
                }, { skipPrefs: true }).catch(() => {});

                // Agencies: say plainly which plan they're on and where to
                // change it. The plan step used to imply they'd chosen one —
                // now they just start on Starter, so tell them rather than
                // leaving them to infer it from a case-slot limit later.
                if (isAgencyRole) {
                  createNotification({
                    user_id:    userId,
                    type:       "system",
                    title:      (() => {
                      const ap = getAgencyPricing(i18n.language);
                      return ap.isPromoActive
                        ? t('notifications.agencyPlanStarterTitle', { endDate: ap.endDate })
                        : t('notifications.agencyPlanStarterTitlePostPromo');
                    })(),
                    body:       (() => {
                      const ap = getAgencyPricing(i18n.language);
                      const plan = { starter: ap.starterMo, starterIncluded: ap.starterIncluded, pro: ap.proMo, proCases: ap.proCases };
                      return ap.isPromoActive
                        ? t('notifications.agencyPlanStarterBody', { ...plan, count: ap.promoCount, endDate: ap.endDate, startDate: ap.startDate })
                        : t('notifications.agencyPlanStarterBodyPostPromo', plan);
                    })(),
                    // link_data.view — NOT action_url. There is no action_url
                    // column; create_notification_for_user only takes
                    // p_link_data, so an action_url is silently dropped and the
                    // notification just marks itself read on click. Notifications
                    // reads link_data.view (normalizeNotification -> linkView).
                    link_data:  { view: "billing" },
                    dedupe_key: `onboarding_plan_${userId}`,
                    read:       false,
                  }, { skipPrefs: true }).catch(() => {});
                }
              } catch (err) {
                console.error("Failed to save onboarding data:", err);
                throw err; // keep the modal open — Onboarding surfaces this
              }
            }
            // Success (or no userId to save against) — safe to close now.
            setShowOnboarding(false);
          }}
          onSkip={() => setShowOnboarding(false)}
        />
        </Suspense>
      )}
      </Suspense>
      {/* Singleton Stripe Checkout modal host — any component that calls
          openStripeCheckout({...}) from stripeService gets routed here. See
          components/StripeCheckoutModal.jsx for the engine pattern. */}
      <StripeCheckoutHost />
      {/* Singleton in-app payment-method update modal host. Any component
          can call openPaymentMethodModal({...}) from stripeService and the
          embedded Stripe Elements form appears here. Replaces the Customer
          Portal redirect for card updates (Slice Q). */}
      <PaymentMethodHost />
    </div>
    </ErrorBoundary>
  );
}
